/**
 * Root-hub click-through links into each settlement subsite.
 *
 * Built as DOM nodes (createElement + textContent) rather than an HTML string,
 * so the static markup is not flagged as an HTML-injection sink by code
 * scanning. Output mirrors the approved markup: two `div.row.mb-5` blocks, each
 * `div.col-… > div.px-3.text-center > h3` carrying a settlement name in bold and
 * a "here" link into the matching subsite.
 *
 * @module rootHub
 */

/**
 * @typedef {Object} HubLinkSpec
 * @property {string} lead - Sentence text before the bold settlement names.
 * @property {string} settlements - Bold settlement names.
 * @property {string} href - Subsite path the "here" link points to.
 * @property {string} colMargin - Bootstrap margin utility for the column.
 */

/** @type {HubLinkSpec[]} */
const HUB_LINKS = [
    {
        lead: "If you are looking for information on the",
        settlements: "Caltech and Johns Hopkins Settlements",
        href: "/caltech-johns-hopkins-settlement",
        colMargin: "mb-1",
    },
    {
        lead: "If you are looking for information on the ",
        settlements:
            "Brown, Chicago, Columbia, Dartmouth, Duke, Emory, Northwestern, Rice, Vanderbilt, and Yale Settlements",
        href: "/university-of-chicago-settlement",
        colMargin: "mb-3",
    },
];

/**
 * Build a single hub-link row element.
 * @param {HubLinkSpec} spec - The row's text, link target, and column margin.
 * @returns {HTMLDivElement} The `div.row.mb-5` row element.
 */
function buildRow(spec) {
    const strong = document.createElement("strong");
    strong.textContent = spec.settlements;

    const link = document.createElement("a");
    link.setAttribute("href", spec.href);
    link.textContent = "here";

    const heading = document.createElement("h3");
    heading.append(
        spec.lead,
        document.createElement("br"),
        strong,
        ", please click ",
        link,
        "."
    );

    const inner = document.createElement("div");
    inner.className = "px-3 text-center";
    inner.append(heading);

    const col = document.createElement("div");
    col.className = `col-lg-12 col-md-12 offset-md-0 ${spec.colMargin}`;
    col.append(inner);

    const row = document.createElement("div");
    row.className = "row mb-5";
    row.append(col);
    return row;
}

/**
 * Build the hub-link row elements.
 * @returns {HTMLDivElement[]} The row elements, in display order.
 */
export function buildRootHubLinks() {
    return HUB_LINKS.map(buildRow);
}

/**
 * Insert the hub links immediately after the given hero element (so they sit
 * between the official-settlement hero and the page alert). No-op if the hero
 * is absent.
 *
 * @param {Element|null} hero - The `#official-settlement-website-hero` element.
 * @returns {void}
 */
export function insertRootHubLinks(hero) {
    if (!hero) {
        return;
    }
    hero.after(...buildRootHubLinks());
}
